import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { HOME_ROUTE, RECOMMENDATION_ROUTE } from '@/constants/routes';
import { useTranslation } from '@/utils/i18n';
import { BackButton, PrimaryButton } from '@/components/Buttons';
import { cn } from '@/lib/utils';
import { useGroups } from '@/hooks/queries/groups';
import { useRecoDraft } from '@/stores/recoDraft';
import { useUiSignals } from '@/stores/uiSignals';
import { createRecommendation } from '@/api/reco';
import { keysAfterRecoShared } from '@/utils/recoShared';
import { updateTrackWithDeezer } from '@/api/track';
import { transformTrackForBackend, getTrackImageUrl } from '@/utils/imageHelpers';
import { getPreviewUrl, useAudioPlayer } from '@/stores/audioPlayer';
import { toast } from '@/stores/toast';
import type { Track } from '@/types';
import TabBarSpacer from '@/components/navigation/TabBarSpacer';
import RoundPlayButton from '@/components/RoundPlayButton';

/**
 * Reco share step (wireframe 5) — choose circle(s) + comment, then submit.
 * Circles come from the user's groups; the track from the reco draft store
 * (set on the search screen). Ported from the legacy `(tabs)/reco/share.tsx`.
 */
export const Route = createFileRoute('/_tabs/reco-share')({
  component: RecoShareScreen,
});

const RACORE_OFFICIAL_SLUG = 'racore-official';
const PUBLIC_CIRCLE_ID = -1; // synthetic "Racore" public circle (until Groups ships)

interface CircleVM {
  id: number;
  name: string;
  emoji: string | null;
  slug: string;
}

function RecoShareScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { track, source, parentRecommendationId } = useRecoDraft();
  const { groups, isLoading: groupsLoading } = useGroups();
  // Must be called unconditionally (before the `if (!track) return null` guard)
  // — hooks have to run in the same order on every render.
  const { currentId, isPlaying, toggle: togglePlay } = useAudioPlayer();

  const [selected, setSelected] = useState<number[]>([]);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const racoreOfficial = useMemo(
    () => groups.find((g) => g.slug === RACORE_OFFICIAL_SLUG) ?? null,
    [groups],
  );

  // Once a reco is submitted we intentionally clear the draft (track → null);
  // this guard stops the redirect below from bouncing us to the search screen
  // instead of Home.
  const submittedRef = useRef(false);

  // No track picked (e.g. deep link / refresh) → back to the share screen. Skipped
  // after a successful submit, where the draft is cleared on purpose.
  useEffect(() => {
    if (!track && !submittedRef.current) navigate({ to: RECOMMENDATION_ROUTE, replace: true });
  }, [track, navigate]);

  const defaultCircleId = racoreOfficial?.id ?? PUBLIC_CIRCLE_ID;

  // Preselect the default circle (real Racore Official, or the public fallback).
  useEffect(() => {
    if (selected.length === 0) setSelected([defaultCircleId]);
  }, [defaultCircleId, selected.length]);

  const displayGroups = useMemo<CircleVM[]>(() => {
    if (groups.length > 0) {
      const official = racoreOfficial ? [racoreOfficial] : [];
      const rest = groups.filter((g) => g.id !== racoreOfficial?.id && g.isActive !== false);
      return [...official, ...rest].map((g) => ({
        id: g.id,
        name: g.name,
        emoji: g.emoji,
        slug: g.slug,
      }));
    }
    // Groups feature unavailable → single public "Racore" circle.
    return [{ id: PUBLIC_CIRCLE_ID, name: 'Racore', emoji: '🌍', slug: RACORE_OFFICIAL_SLUG }];
  }, [groups, racoreOfficial]);

  const createReco = useMutation({
    mutationFn: () =>
      createRecommendation({
        track: transformTrackForBackend(track) as unknown as Track,
        comment: { content: comment, community: true },
        source,
        // Drop the synthetic public id → empty groupIds = public reco.
        groupIds: selected.filter((id) => id !== PUBLIC_CIRCLE_ID),
        parentRecommendationId: parentRecommendationId ?? undefined,
      }),
    onSuccess: () => {
      // Mark submitted BEFORE clearing the draft so the "no track → search"
      // effect is suppressed and we land on Home.
      submittedRef.current = true;
      useRecoDraft.getState().clear();
      // Land on the first circle (in display order) the song was shared with, so
      // the new reco is actually visible — not always the public Racore feed.
      const landOn =
        displayGroups.find((g) => selected.includes(g.id) && g.id !== PUBLIC_CIRCLE_ID)?.id ?? null;
      useUiSignals.getState().setLandOnCircle(landOn);
      // Reload every circle feed (the new reco shows first) and the circle list
      // (the circle moves up), then scroll to the top.
      for (const queryKey of keysAfterRecoShared())
        void queryClient.invalidateQueries({ queryKey });
      useUiSignals.getState().bumpHomeScrollTop();
      const idx = Math.floor(Math.random() * 9);
      toast.success(t(`reco.success.${idx}.title`), { icon: t(`reco.success.${idx}.icon`) });
      navigate({ to: HOME_ROUTE, replace: true });
    },
    onError: () => toast.error(t('toast.recoShareFailed')),
  });

  const toggle = (id: number) => {
    setError('');
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleReco = () => {
    if (selected.length === 0) {
      setError(t('reco.error.groupRequired'));
      return;
    }
    createReco.mutate();
  };

  if (!track) return null;

  const image = getTrackImageUrl(track);
  const artists = track.artists?.map((a) => a.name).join(', ');
  const preview = getPreviewUrl(track);
  const audioId = `reco-${track.isrc || track.id}`;
  const playing = currentId === audioId && isPlaying;

  return (
    <div
      className="tw:flex tw:h-full tw:flex-col tw:overflow-y-auto tw:px-5 tw:pb-6"
      data-testid="screen-reco-share"
    >
      <BackButton
        onClick={() => navigate({ to: RECOMMENDATION_ROUTE })}
        data-testid="btn-reco-back"
        className="tw:mt-3"
      />

      <h1 className="tw:mb-4 tw:mt-4 tw:heading-2 tw:text-white">{t('reco.title')}</h1>

      {/* Track preview */}
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
        <div className="tw:relative tw:size-32 tw:overflow-hidden tw:rounded-2xl tw:bg-dark-1">
          {image && <img src={image} alt="" className="tw:size-full tw:object-cover" />}
          {/* Play / pause — bottom-right (matches the home card) */}
          <RoundPlayButton
            audioId={audioId}
            size={40}
            playing={playing}
            disabled={!preview}
            onToggle={() => togglePlay(preview, audioId, () => updateTrackWithDeezer(track.isrc))}
            testId="btn-reco-play"
            className="tw:absolute tw:bottom-2 tw:right-2"
          />
        </div>
        <p className="tw:text-base tw:font-semibold tw:text-white">{track.title}</p>
        <p className="tw:caption tw:text-grey-3">{artists}</p>
      </div>

      {/* Circle grid */}
      <p className="tw:mb-1 tw:mt-6 tw:text-[15px] tw:font-semibold tw:text-white">
        {t('reco.community')}
      </p>
      {error && <p className="tw:mb-1 tw:text-[12px] tw:text-error">{error}</p>}

      {groupsLoading ? (
        <div className="tw:mt-4 tw:flex tw:justify-center">
          <span className="tw:size-6 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/20 tw:border-t-blue-primary" />
        </div>
      ) : (
        <div className="tw:flex tw:flex-wrap">
          {displayGroups.map((g) => {
            const isSelected = selected.includes(g.id);
            const isOfficial = g.slug === RACORE_OFFICIAL_SLUG;
            const label = isOfficial ? 'Racore' : g.name;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => toggle(g.id)}
                data-testid={`circle-${g.id}`}
                className="tw:flex tw:w-1/4 tw:flex-col tw:items-center tw:gap-1 tw:py-2.5"
              >
                <span
                  className={cn(
                    'tw:flex tw:size-16 tw:items-center tw:justify-center tw:rounded-full tw:bg-dark-1',
                    g.emoji ? 'tw:text-[28px]' : 'tw:text-[22px] tw:font-bold tw:text-white',
                    isSelected && 'tw:border-[2.5px] tw:border-green-3',
                  )}
                >
                  {g.emoji || label.charAt(0).toUpperCase()}
                </span>
                <span
                  className={cn(
                    'tw:max-w-[64px] tw:truncate tw:text-[11px]',
                    isSelected ? 'tw:font-semibold tw:text-white' : 'tw:text-grey-4',
                  )}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Comment + CTA */}
      <div className="tw:mt-auto tw:flex tw:flex-col tw:gap-5 tw:pt-6">
        <input
          type="text"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onFocus={(e) =>
            setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
          }
          maxLength={300}
          placeholder={t('reco.comment')}
          data-testid="input-reco-comment"
          className="tw:h-14 tw:w-full tw:rounded-2xl tw:border tw:border-blue-2/40 tw:bg-dark-1 tw:px-4 tw:text-base tw:text-white tw:placeholder:text-grey-4"
        />
        <div className="tw:mx-auto tw:w-full tw:max-w-[280px]">
          <PrimaryButton
            label={t('reco.callToAction')}
            onClick={handleReco}
            loading={createReco.isPending}
            data-testid="btn-reco-submit"
          />
        </div>
      </div>
      <TabBarSpacer />
    </div>
  );
}
